import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { useAccount } from '../WalletProvider';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';

const NavContainer = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
`;

const WalletButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font-family: 'M PLUS Rounded 1c', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.sumi};
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 
    0 2px 8px rgba(0, 0, 0, 0.05),
    0 1px 2px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 
      0 4px 12px rgba(0, 0, 0, 0.1),
      0 2px 3px rgba(0, 0, 0, 0.1);
  }
`;

const Balance = styled.span`
  background: rgba(255, 255, 255, 0.9);
  padding: 8px 16px;
  border-radius: 20px;
  font-family: 'Space Mono', monospace;
  font-size: 14px;
  color: ${theme.colors.sumi};
  box-shadow: 
    0 2px 8px rgba(0, 0, 0, 0.05),
    0 1px 2px rgba(0, 0, 0, 0.1);
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  padding: 8px;
  box-shadow: 
    0 4px 20px rgba(0, 0, 0, 0.1),
    0 2px 8px rgba(0, 0, 0, 0.05);
  opacity: ${props => props.$isOpen ? 1 : 0};
  transform: translateY(${props => props.$isOpen ? '0' : '-8px'});
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  transition: all 0.2s;
  z-index: 1000;
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 8px 16px;
  background: none;
  border: none;
  border-radius: 8px;
  font-family: 'M PLUS Rounded 1c', sans-serif;
  font-size: 14px;
  color: ${theme.colors.sumi};
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  white-space: nowrap;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

export function Navigation() {
  const { account, disconnect } = useAccount();
  const { setVisible } = useWalletModal();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!account) return null;

  const shortAddress = `${account.wallet_address.slice(0, 4)}...${account.wallet_address.slice(-4)}`;
  
  return (
    <NavContainer>
      <Balance>{account.balance} credits</Balance>
      <WalletButton 
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
      >
        {shortAddress}
      </WalletButton>
      <DropdownMenu ref={dropdownRef} $isOpen={isOpen}>
        <MenuItem onClick={() => setVisible(true)}>
          Change Wallet
        </MenuItem>
        <MenuItem onClick={() => {
          disconnect();
          setIsOpen(false);
        }}>
          Disconnect
        </MenuItem>
      </DropdownMenu>
    </NavContainer>
  );
} 